import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, LogOut, Home, Settings, Wrench, AlertCircle } from 'lucide-react';

const UserLoginStatusInfolet = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Check if user has administrator or super_administrator role
  const isAdmin = user?.roles?.includes('administrator') || user?.roles?.includes('super_administrator');

  // Check if user has unverified role
  const isUnverified = user?.roles?.includes('unverified');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
    // Full page redirect to landing page (not React Router navigation)
    window.location.href = '/';
  };

  const handleNavigate = (path) => {
    setIsOpen(false);
    navigate(path);
  };

  const menuItems = [
    { label: 'Dashboard', icon: Home, path: '/user', onClick: () => handleNavigate('/user') },
    { label: 'Account', icon: User, path: '/user/account', onClick: () => handleNavigate('/user/account'), showWarning: isUnverified },
  ];

  if (isAdmin) {
    menuItems.push({
      label: 'Admin',
      icon: Settings,
      path: '/admin/dashboard',
      onClick: () => handleNavigate('/admin/dashboard')
    });
  }

  menuItems.push({
    label: 'Logout',
    icon: LogOut,
    onClick: handleLogout,
    isDanger: true
  });

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '8px 12px',
          borderRadius: '6px',
          transition: 'background 0.2s',
          color: '#333'
        }}
        onMouseOver={(e) => e.currentTarget.style.background = '#f5f5f5'}
        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <User size={20} />
        <span style={{ fontWeight: '500' }}>{user?.name}</span>
        {isAdmin && <Wrench size={16} style={{ color: '#667eea' }} />}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '4px',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          border: '1px solid #e0e0e0',
          minWidth: '180px',
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={item.onClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '14px',
                  color: item.isDanger ? '#dc3545' : '#333',
                  borderTop: index > 0 ? '1px solid #f0f0f0' : 'none',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#f8f9fa'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Icon size={16} />
                {item.label}
                {item.showWarning && (
                  <AlertCircle size={16} style={{ color: '#f59e0b', marginLeft: 'auto' }} title="Email not verified" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserLoginStatusInfolet;

